import React from 'react';
import { Building2 } from 'lucide-react';

const TEAL = '#02857E';

interface BrokerPlanGuidanceBannerProps {
  /** True when an invite token is present (URL param or stashed). */
  invited?: boolean;
  /** Inviting shipper's display company name, when resolved. */
  shipperName?: string | null;
  /** Kept for backwards compatibility; the banner CTA is now a non-clickable label. */
  onJumpToPrivate?: () => void;
  className?: string;
}

/**
 * "Which plan do I need?" callout rendered directly above the broker pricing
 * cards on both /brokers and /pricing (Broker tab). Answers the biggest broker
 * hesitation — $99.99 or $249.99 — before they read the cards.
 */
const BrokerPlanGuidanceBanner: React.FC<BrokerPlanGuidanceBannerProps> = ({
  invited = false,
  shipperName,
  className,
}) => {
  const heading = invited
    ? `You were invited by ${shipperName || 'a shipper'}.`
    : 'Already have a shipper invitation?';
  const body = invited
    ? 'Your invitation is saved. Full Access is the plan required to accept the invitation and start receiving their freight.'
    : 'If a shipper invited you to their private network, Full Access is the plan required to accept the invitation and start receiving their freight.';

  return (
    <div
      className={`max-w-6xl mx-auto mb-6 rounded-2xl border-2 bg-[#f1faf9] px-4 py-4 sm:px-6 sm:py-5 ${className || ''}`}
      style={{ borderColor: invited ? TEAL : 'rgba(2,133,126,0.35)' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div
          className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: TEAL }}
        >
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-[#0a3d3a]">{heading}</p>
          <p className="text-sm text-[#155f5a] mt-1">{body}</p>
        </div>
        <span
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white shadow-sm flex-shrink-0 select-none pointer-events-none"
          style={{ backgroundColor: TEAL }}
        >
          {invited ? 'Accept your invitation' : 'Join their network'}
        </span>
      </div>
    </div>
  );
};

export default BrokerPlanGuidanceBanner;

